import "./Queue.css";
import { Divider, Dropdown, Menu, Space } from "antd";
import { Form } from "antd";
import { useEffect, useState, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import React from 'react';
import { t} from "i18next";
import Selector from "components/Selector/Selector";
import { QueueSettingsProps, TopicConfig} from "./interfaces";
import Spinner from "components/Spinner/Spinner";
import TextInput from "components/Inputs/TextInput/TextInput";
import PrimaryButton from "components/Buttons/PrimaryButton/PrimaryButton";
import { ConfigTypeEnum, QueueTypeEnum } from "utils/enums";
import centralApi from "services/centralApi";
import {  InfrastructureSettings} from "utils/models";
import { deserialize, serialize } from "utils/models/serializer";
import { API_ENDPOINTS } from "utils/constants";
import { getToken } from "redux/features/app/authTokenSlice";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import useMessage from "hooks/useMessage";
import { BOOLEAN_OPTIONS } from "utils/constants/selectorOptions";


const Queue: React.FC<QueueSettingsProps> = ({
  currentAppKey,
  updateCurrentAppKey
}) => {
  const [loading, setLoading] = useState(BOOLEAN_OPTIONS.FALSE);
  const [form] = Form.useForm();
  const dispatch: any = useDispatch();
  
  const loginUser = useSelector((state: any) => state.loginUser);
  const activeApp = useSelector((state: any) => state.activeApp);
  const authToken = useSelector((state: any) => state.authToken.token);
  const [topics, setTopics] = useState<any[]>([]);
   const [queues, setQueues] = useState<any[]>([]);
  const [defaultPartitionCount, setDefaultPartitionCount] = useState<number>(0);
  const { showError, showSuccess } = useMessage();
  const appId = activeApp.appId;
  const api_key = loginUser.data.api_key;

  //Add topic module
    const handleAddTopic = () => {
    const newTopic: any = {
      name: '',
      preferredQueues:[],
      partitionCount: 0,

    };
    setTopics([...topics, newTopic]);
     form.setFieldsValue({
      topics: [...topics,newTopic ]
    });
  };

  //removes topic module
  const handleRemoveTopic = (index: number) => {
    const updatedTopics = [...topics];
    updatedTopics.splice(index, 1);
    setTopics(updatedTopics);
    form.setFieldsValue({ topics: updatedTopics });
  };

  //handleTopicChange takes index and sets its properties
  const handleTopicChange = (index: number, field: any, value: string | number | string[]) => {
    const updatedTopics = [...topics];
    updatedTopics[index] = {
      ...updatedTopics[index],
      [field]: field === 'partitionCount' ? Number(value) : value
    };
    setTopics(updatedTopics);
    form.setFieldsValue({ topics: updatedTopics });
  };

  const transformTopicsToConfig = (topics: any[]): TopicConfig => {
    const topicConfig: TopicConfig = {};
    topics.forEach(topic => {
      if (topic.name) { // Only add if name is not empty
        topicConfig[topic.name] = {
          preferredQueues: topic.preferredQueues,
          partitionCount: topic.partitionCount
        };
      }
    });
    return topicConfig;
  };

  //  Add Queue Dropdown logic
  const handleAddQueue = (type: string) => {
    const count = queues?.filter(q => q.type === type).length + 1;
    const newQueue =  {
        id: `${type}_${count}`,
        name: '',
        type,
        config: {},
      }
    setQueues([...queues,newQueue ]);
     form.setFieldsValue({ queues: [...queues,newQueue]
    });
  };

  //  Handle queue field changes
  const handleQueueChange = (idx: number, field: string, value: any) => {
    const updated = [...queues];
    if (field === 'name') {
      updated[idx][field] = value;
    } else {
      updated[idx].config[field] = value;
    }
    setQueues(updated);
    form.setFieldsValue({ queues: updated });
  };

  //  Remove queue and re-index
  const handleRemoveQueue = (idx: number, type: string) => {
    let updated = queues.filter((_, i) => i !== idx);
    // Re-index IDs for this type
    let count = 1;
    updated = updated.map(q => {
      if (q.type === type) {
        return { ...q, id: `${type}_${count++}` };
      }
      return q;
    });
    setQueues(updated);
    form.setFieldsValue({ queues: updated });
  };


  // uses the infrastructure model class for type handling and call the api
  const onFormSubmit = async () => {
    let formData = new InfrastructureSettings();
    const topicsConfig = topics.length > 0 ? transformTopicsToConfig(topics) : undefined;
    
    formData = {
      queueSettings: {
      defaultPartitionCount,
      queues,
      topicsConfig
      }
    };

   const queueConfig = deserialize(formData, InfrastructureSettings);
   const serializedQueueData = serialize(queueConfig as InfrastructureSettings);
    const payload = {
        args: JSON.stringify({
            app_id: appId,
            configType: ConfigTypeEnum.QUEUE,
            ...serializedQueueData,
        }),
        app_id: appId,
        api_key: api_key,
        authToken
    };

    await updateGCMKeys(payload);
    dispatch(getToken())
    
};

  async function updateGCMKeys(formData: any) {
    const url: string = API_ENDPOINTS.UPDATE_GCM_KEYS;

    try {
      setLoading(BOOLEAN_OPTIONS.TRUE);
      await centralApi("POST", url, formData, null);
      showSuccess(t("sucessfullySavedLabel"));
      setLoading(BOOLEAN_OPTIONS.FALSE);
      updateCurrentAppKey();
    } catch (error) {
      setLoading(BOOLEAN_OPTIONS.FALSE);
      showError(t("somethingWrongLabel"));
    }
  }

 useEffect(() => {
    const queues = currentAppKey[0]?.infrastructureSettings?.queueSettings?.queues as any[];
    const defaultPartitionCount = currentAppKey[0]?.infrastructureSettings?.queueSettings?.defaultPartitionCount;
    const existingTopicsConfig = currentAppKey[0]?.infrastructureSettings?.queueSettings?.topicsConfig as TopicConfig || {};

    // Convert the topic config back to array format for the form
    const existingTopics = Object.entries(existingTopicsConfig).map(([name, config]) => ({
      name,
      preferredQueues: config.preferredQueues,
      partitionCount: config.partitionCount
    }));
     form.setFieldsValue({
      defaultPartitionCount,
      queues,
      topics: existingTopics?.length > 0 ? existingTopics : []
    });

    setTopics(existingTopics?.length > 0 ? existingTopics : []);
    setDefaultPartitionCount(defaultPartitionCount);
    setQueues(queues?.length > 0 ? queues : [])
 },[ currentAppKey, form])

  return (
    <Fragment>
      <div className="queue-notification-container">
        <div className="mt-4 mb-4  ">
          <h6 className="queue-notification-container-heading">
            Queue Service
          </h6>
          <Divider className="queue-notification-custom-divider" />
        </div>
        {loading ? (
          <Spinner />
        ) : (
          <>
            <Form
              form={form}
              layout="vertical"
              onFinish={onFormSubmit}
            >
                <Space  className="queue-flex-space" align="baseline">
              <Form.Item
                label={t("partitionCountLabel")}
                name={"defaultPartitionCount"}
                rules={[
                  { required: true, message: "Please select Default Partition Count" },
                ]}
                initialValue={defaultPartitionCount}
              >
                <TextInput
                  type="number"
                  value={defaultPartitionCount}
                  onChange={(e: any) => setDefaultPartitionCount(Number(e.target.value))}
                />
              </Form.Item>
              </Space>


            {/* Conditional Rendering based on storageProviderType */}

             {queues?.map((queue, idx) => (
              <Space key={queue.id} className="queue-flex-space-mb" align="baseline">
            <Form.Item
                label={"Queue name"}
                name={['queues', idx, 'name']}
                rules={[
                  { required: true, message: "Please name" },
                ]}
              >
                <TextInput 
                  value={queue.name}
                  onChange={(e:any) => handleQueueChange(idx, 'name', e.target.value)} 
                />
              </Form.Item>
            {queue.type ===  QueueTypeEnum.PG && (
             <>
              <Form.Item
                label={t("userlabel")}
                name={['queues', idx, 'config', 'user']}
                rules={[
                  { required: true, message: "Please Select PG Queue User" },
                ]}
              >
                <TextInput value={queue.config.user} onChange={(e:any) => handleQueueChange(idx, 'user', e.target.value)} />
              </Form.Item>
              <Form.Item
                label={t("hostLabel")}
                name={['queues', idx, 'config', 'host']}
                rules={[
                  { required: true, message: "Please Select PG Queue Host" },
                ]}
              >
                <TextInput value={queue.config.host} onChange={(e:any) => handleQueueChange(idx, 'host', e.target.value)} />
              </Form.Item>
              <Form.Item
                label={t("portLabel")}
                 name={['queues', idx, 'config', 'port']}
                rules={[
                  { required: true, message: "Please Select PG Queue  Port" },
                ]}
                initialValue={""}
              >
                <TextInput type="number" value={queue.config.port} onChange={(e:any) => handleQueueChange(idx, 'port', e.target.value)}/>
              </Form.Item>
              <Form.Item
                label={t("databaseLabel")}
                name={['queues', idx, 'config', 'database']}
                rules={[
                  { required: true, message: "Please Select PG Queue Database" },
                ]}
              >
                <TextInput value={queue.config.database} onChange={(e:any) => handleQueueChange(idx, 'database', e.target.value)} />
              </Form.Item>
              <Form.Item
                label={t("passwordLabel")}
                name={['queues', idx, 'config', 'password']}
                rules={[
                  { required: true, message: "Please Select PG Queue Password" },
                ]}
              >
                <TextInput type="password" visibilityToggle={false} value={queue.config.password} onChange={(e:any) => handleQueueChange(idx, 'password', e.target.value)}/>
              </Form.Item>
              </>
            )}
              {(queue.type  == QueueTypeEnum.AZURE &&
              <>
              <Form.Item
                label={t("azureApiQueueSasUrl")}
                name={['queues', idx, 'config', 'azureApiQueueSasUrl']}
                rules={[
                  { required: true, message: "Please Select Azure Queue SAS Url" },
                ]}
              >
                <TextInput className="queue-azure-url-input" value={queue.config.azureApiQueueSasUrl} onChange={(e:any) => handleQueueChange(idx, 'azureApiQueueSasUrl', e.target.value)}/>
              </Form.Item>
              <Form.Item
                label={t("sasTokenLabel")}
                name={['queues', idx, 'config', 'azureApiQueueSasToken']}
                rules={[
                  { required: true, message: "Please Select Azure Queue SAS Token" },
                ]}
              >
                <TextInput type="password" visibilityToggle={false} className="queue-azure-token-input" value={queue.config.azureApiQueueSasToken} onChange={(e:any) => handleQueueChange(idx, 'azureApiQueueSasToken', e.target.value)}  />
              </Form.Item>
              </>
              )}
                <MinusCircleOutlined  onClick={() => handleRemoveQueue(idx, queue.type)} />
              
             </Space>
                ))}
     
              <Form.Item>
                <Dropdown
                  overlay={
                    <Menu onClick={({ key }) => handleAddQueue(key)}>
                      <Menu.Item key={QueueTypeEnum.PG}>{t("pgLabel")}</Menu.Item>
                      <Menu.Item key={QueueTypeEnum.AZURE}>{t("azureLabel")}</Menu.Item>
                    </Menu>
                  }
                  trigger={['click']}
                >
                  <PrimaryButton type="primary" icon={<PlusOutlined />}>{t("addQueuesLabel")}</PrimaryButton>
                </Dropdown>
          </Form.Item>
                
              <Divider className="queue-notification-custom-divider" />
              <div className="mt-4 mb-4">
                <h6 className="queue-notification-container-heading">
                  Topic Config
                </h6>
                <p className="configure-columns-form-headings">{t("topicConfigLabel")}</p>
              </div>
              <div>
                {topics.length > 0 && (
                  <>
                    {topics.map((topic, index) => (
                      <Space key={index} className="queue-flex-space" align="baseline">
                        <Form.Item
                          rules={[{ required: true, message: "Please add topic name" }]}
                        >
                          <TextInput
                            className="queue-topic-name-input"
                            placeholder="Topic Name"
                            name={['topics', index, 'name']}
                            value={topic.name}
                            onChange={(e:any) => handleTopicChange(index, 'name', e.target.value)}
                          />
                        </Form.Item>
                        <Form.Item
                          name={['topics', index, 'partitionCount']}
                          rules={[{ required: true, message: "Please add partition count" }]}
                        >
                          <TextInput
                            placeholder="Partition count"
                            type="number"
                            min={0}
                            value={topic.partitionCount}
                            onChange={(e:any) => handleTopicChange(index, 'partitionCount', e.target.value)}
                          />
                        </Form.Item>
                        <Form.Item
                          name={['topics', index, 'preferredQueues']}
                          rules={[{ required: true, message: "Please select preferred queues" }]}
                        >
                          <Selector
                            mode="multiple"
                            options={queues.map(q => ({ label: q.name || q.id, value: q.id }))}
                             className="queue-preferred-queues-selector"
                            value={topic.preferredQueues}
                            onChange={(val: string[]) => handleTopicChange(index, 'preferredQueues', val)}
                            placeholder="Preferred Queues"
                          />
                        </Form.Item>
                          <MinusCircleOutlined
                            onClick={() => handleRemoveTopic(index)}
                          />
                      </Space>
                    ))}
                  </>
                )}

                <Form.Item>
                  <PrimaryButton
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddTopic}
                  >
                    {t("addTopicsLabel")}
                  </PrimaryButton>
                </Form.Item>

                <div className="d-flex mb-1">
                  <PrimaryButton
                    size="middle"
                    type="primary"
                    htmlType="submit"
                    className="queue-btn-save"
                  >
                    Save
                  </PrimaryButton>
                </div>
              </div>
            </Form>
          </>
        )}
      </div>
    </Fragment>
  );
};
export default Queue;
